import { Dictionary, uniqBy } from 'lodash';
import { Role, Team } from 'api/models';
import { Project } from 'api/project';
import { StatusFieldOption } from 'api/tasks';
import {
  ProjectUser,
  ProjectsWithMembers,
  ProjectWithUserGroupedByRole,
  RoleWithUsersAndTeams,
  TeamWithProject,
  TeamWithProjectGroupedByRoleLookup,
} from '../types';

export const getProjectsOfUser = (
  projects: Project[],
  userId: number,
): Project[] =>
  projects.filter(({ teamMembers }) =>
    teamMembers?.map(({ userId: x }) => x).includes(userId),
  );

export const getUsersAndTeamsGroupByRole = (
  projectMembers: ProjectUser[],
  teams: Team[],
  roles: Role[],
): RoleWithUsersAndTeams[] => {
  const rolesFromUsers = projectMembers.reduce(
    (result, { role }) => result.concat(role ?? []),
    [] as Role[],
  );
  const rolesToDisplay = uniqBy(roles.concat(rolesFromUsers), 'roleId');

  return rolesToDisplay.map((role) => ({
    ...role,
    projectMembers: projectMembers.filter(
      ({ user: { role: userRole }, role: memberRole }) =>
        memberRole
          ? memberRole?.roleId === role.roleId
          : userRole?.roleId === role.roleId,
    ),
    teams: teams.filter(
      ({ roleId, resolved }) =>
        roleId && roleId === role.roleId && resolved !== true,
    ),
  }));
};

export const getProjectsFilteredByCurrentUser = (projects, currentUser) =>
  projects.reduce((acc, curr) => {
    const filteredProjectMembers = curr.projectMembers.filter(
      (user) => user.user.userId === currentUser.user.userId,
    );

    if (filteredProjectMembers.length > 0) {
      return [...acc, { ...curr, projectMembers: filteredProjectMembers }];
    }

    return acc;
  }, []) ?? [];

export const getProjectsWithTeamMembersGroupedByRole = (
  projects: ProjectsWithMembers[],
  roles: Role[],
  teamMembership: Record<string, Team[]>,
): ProjectWithUserGroupedByRole[] => {
  return projects.map((project) => ({
    ...project,
    teamMembersByRoles: getUsersAndTeamsGroupByRole(
      project.projectMembers,
      teamMembership[project.projectId] ?? [],
      roles,
    ),
  }));
};

export const getTeamsWithProject = (
  teamMembership: Record<string, Team[]>,
  projectLookup: Dictionary<Project>,
  teamLookup: Dictionary<Team>,
): TeamWithProject[] => {
  const getTeamWithProject = (
    projectId: number,
    teamId: number,
  ): TeamWithProject => ({
    ...projectLookup[projectId],
    ...teamLookup[teamId],
  });

  return Object.keys(teamMembership).reduce(
    (result, projectId) => [
      ...result,
      ...teamMembership[projectId].map(
        ({ teamId, resolved, userList, companyList }) => ({
          ...getTeamWithProject(parseInt(projectId, 10), teamId),
          userList,
          companyList,
          resolved,
        }),
      ),
    ],
    [] as TeamWithProject[],
  );
};

export const filterUnresolvedVendorResponsibleTeams = (
  teams: TeamWithProject[],
  vendorCompanyId: number,
): TeamWithProject[] =>
  teams.filter(
    ({ resolved, companyList }) =>
      resolved !== true &&
      (companyList?.[0] == null ||
        companyList[0]?.companyId === vendorCompanyId),
  );

export const getTeamsWithProjectGroupedByRole = (
  teamsWithProject: TeamWithProject[],
  roles: Role[],
): TeamWithProjectGroupedByRoleLookup => {
  return roles.reduce((result, { roleId: _roleId }) => {
    const filteredTeamsForRole = teamsWithProject.filter(
      ({ roleId, resolved }) =>
        roleId && roleId === _roleId && resolved !== true,
    );

    return {
      ...result,
      ...(filteredTeamsForRole.length
        ? {
            [_roleId]: filteredTeamsForRole,
          }
        : {}),
    };
  }, {} as TeamWithProjectGroupedByRoleLookup);
};

export const getProjectStatuses = (
  projects: ProjectsWithMembers[],
  statusFieldId?: number,
): Dictionary<StatusFieldOption> => {
  const lookup: Dictionary<StatusFieldOption> = {};

  projects.forEach(({ projectId, fields }) => {
    if (statusFieldId != null) {
      lookup[projectId] = fields.find((x) => x.fieldId === statusFieldId)
        ?.metaFieldValue as StatusFieldOption;
    }
  });

  return lookup;
};
